import React, { useState, useEffect } from 'react';
import { useArtistAuth } from '../../hooks/useArtistAuth';
import { supabase } from '../../utils/supabaseClient';
import { 
  User, 
  Mail, 
  Phone, 
  Globe, 
  Instagram, 
  FileText,
  AlertCircle,
  CheckCircle
} from 'lucide-react';
import '../../styles/SubmissionSystem.css';

const ApplicationForm = ({ submissionData, formData, onChange, onNext }) => {
  const { user } = useArtistAuth();
  const [artistProfile, setArtistProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState({});
  const [isValid, setIsValid] = useState(false);

  useEffect(() => {
    if (user) {
      loadArtistProfile();
    }
  }, [user]);

  useEffect(() => {
    validateForm();
  }, [formData]);

  const loadArtistProfile = async () => {
    try {
      setLoading(true);
      
      // Load user profile data
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('*')
        .eq('uuid', user.id)
        .single();

      if (userError) throw userError;

      // Load artist submission profile
      const { data: artistData, error: artistError } = await supabase
        .from('artist_submission_profiles')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (artistError && artistError.code !== 'PGRST116') {
        console.warn('Could not load artist profile:', artistError);
      }

      // Merge data
      const profile = {
        ...userData,
        ...artistData
      };

      setArtistProfile(profile);
      
      // Pre-fill form with existing data, using bio if available
      if (!formData.artist_name && profile.first_name) {
        onChange({
          artist_name: `${profile.first_name} ${profile.last_name || ''}`.trim(),
          email: profile.email || '',
          phone: profile.mobile_phone || '',
          website: profile.website_url || '',
          instagram: profile.instagram_handle || '',
          artist_statement: profile.artist_statement || profile.bio || '',
          preferred_mediums: profile.preferred_mediums || [],
          years_experience: profile.years_experience || '',
          education: profile.education || '',
          awards: profile.awards || [],
          exhibitions: profile.exhibitions || []
        });
      }

    } catch (error) {
      console.error('Error loading artist profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Always required fields
    if (!formData.artist_name?.trim()) {
      newErrors.artist_name = 'Artist name is required';
    }
    
    if (!formData.email?.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    // Validate configurable sections
    if (submissionData.form_config?.sections?.artist_statement?.enabled && 
        submissionData.form_config.sections.artist_statement.required && 
        !formData.artist_statement?.trim()) {
      newErrors.artist_statement = 'Artist statement is required';
    }
    
    if (submissionData.form_config?.sections?.professional_info?.enabled) {
      const profConfig = submissionData.form_config.sections.professional_info;
      
      if (profConfig.fields?.preferred_mediums?.enabled && 
          profConfig.fields.preferred_mediums.required && 
          (!formData.preferred_mediums || formData.preferred_mediums.length === 0)) {
        newErrors.preferred_mediums = 'Preferred mediums are required';
      }
      
      if (profConfig.fields?.years_experience?.enabled && 
          profConfig.fields.years_experience.required && 
          !formData.years_experience) {
        newErrors.years_experience = 'Years of experience is required';
      }
      
      if (profConfig.fields?.education?.enabled && 
          profConfig.fields.education.required && 
          !formData.education?.trim()) {
        newErrors.education = 'Education is required';
      }
    }
    
    if (submissionData.form_config?.sections?.awards?.enabled && 
        submissionData.form_config.sections.awards.required && 
        (!formData.awards || formData.awards.length === 0)) {
      newErrors.awards = 'At least one award is required';
    }
    
    if (submissionData.form_config?.sections?.exhibitions?.enabled && 
        submissionData.form_config.sections.exhibitions.required && 
        (!formData.exhibitions || formData.exhibitions.length === 0)) {
      newErrors.exhibitions = 'At least one exhibition is required';
    }
    
    setErrors(newErrors);
    setIsValid(Object.keys(newErrors).length === 0);
  };

  const handleInputChange = (field, value) => {
    onChange({ [field]: value });
  };

  const handleArrayChange = (field, value, action = 'set') => {
    let newArray = [...(formData[field] || [])];
    
    switch (action) {
      case 'add':
        if (value && !newArray.includes(value)) {
          newArray.push(value);
        }
        break;
      case 'remove':
        newArray = newArray.filter(item => item !== value);
        break;
      case 'set':
      default:
        newArray = value;
        break;
    }
    
    onChange({ [field]: newArray });
  };

  // Helper function to format dimensions for display
  const formatDimensions = (width, height, depth, unit) => {
    if (!width && !height) return '';
    
    let dimensions = `${width || 0} × ${height || 0}`;
    if (depth && depth > 0) {
      dimensions += ` × ${depth}`;
    }
    dimensions += ` ${unit || 'inches'}`;
    
    return dimensions;
  };

  const handleNext = () => {
    if (isValid) {
      onNext();
    } else {
      // Scroll to first error
      const firstErrorField = Object.keys(errors)[0];
      if (firstErrorField) {
        const element = document.getElementById(firstErrorField);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
    }
  };

  if (loading) {
    return (
      <div className="submission-loading">
        <div className="submission-spinner"></div>
        <span>Loading artist profile...</span>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header - Direct on page like Portfolio Upload */}
      <div className="upload-header">
        <h2 className="upload-title">Application Form</h2>
        <p className="upload-subtitle">
          Please fill out your application details for "{submissionData.title}"
        </p>
      </div>

      {/* Guidelines Display */}
      {submissionData.submission_guidelines && (
        <div className="submission-content-card">
          <h3 className="submission-content-card-title">Submission Guidelines</h3>
          <div 
            className="submission-content-card-body" 
            dangerouslySetInnerHTML={{ __html: submissionData.submission_guidelines }}
          />
        </div>
      )}

      {/* Personal Information */}
      <div className="submission-content-card">
        <div className="submission-content-card-header">
          <h3 className="submission-content-card-title">Personal Information</h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Artist Name */}
          <div className="submission-form-group">
            <label htmlFor="artist_name" className="submission-form-label">
              Artist Name *
            </label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                id="artist_name"
                type="text"
                value={formData.artist_name || ''}
                onChange={(e) => handleInputChange('artist_name', e.target.value)}
                className={`submission-form-input pl-10 ${
                  errors.artist_name ? 'error' : ''
                }`}
                placeholder="Enter your full name"
              />
            </div>
            {errors.artist_name && (
              <p className="submission-form-error">
                <AlertCircle className="w-4 h-4 mr-1" />
                {errors.artist_name}
              </p>
            )}
          </div>

          {/* Email */}
          <div className="submission-form-group">
            <label htmlFor="email" className="submission-form-label">
              Email Address *
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                id="email"
                type="email"
                value={formData.email || ''}
                onChange={(e) => handleInputChange('email', e.target.value)}
                className={`submission-form-input pl-10 ${
                  errors.email ? 'error' : ''
                }`}
                placeholder="Enter your email address"
              />
            </div>
            {errors.email && (
              <p className="submission-form-error">
                <AlertCircle className="w-4 h-4 mr-1" />
                {errors.email}
              </p>
            )}
          </div>

          {/* Phone */}
          <div className="submission-form-group">
            <label htmlFor="phone" className="submission-form-label">
              Phone Number
            </label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                id="phone"
                type="tel"
                value={formData.phone || ''}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                className="submission-form-input pl-10"
                placeholder="Enter your phone number"
              />
            </div>
          </div>

          {/* Website */}
          <div className="submission-form-group">
            <label htmlFor="website" className="submission-form-label">
              Website
            </label>
            <div className="relative">
              <Globe className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                id="website"
                type="url"
                value={formData.website || ''}
                onChange={(e) => handleInputChange('website', e.target.value)}
                className="submission-form-input pl-10"
                placeholder="https://yourwebsite.com"
              />
            </div>
          </div>

          {/* Instagram */}
          <div className="submission-form-group">
            <label htmlFor="instagram" className="submission-form-label">
              Instagram Handle
            </label>
            <div className="relative">
              <Instagram className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                id="instagram"
                type="text"
                value={formData.instagram || ''}
                onChange={(e) => handleInputChange('instagram', e.target.value)}
                className="submission-form-input pl-10"
                placeholder="@yourhandle"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Artist Statement */}
      {submissionData.form_config?.sections?.artist_statement?.enabled && (
        <div className="submission-content-card">
          <div className="submission-content-card-header">
            <h3 className="submission-content-card-title">
              {submissionData.form_config.sections.artist_statement.label || 'Artist Statement'}
            </h3>
          </div>
          
          <div className="submission-content-card-body">
            <div className="submission-form-group">
              <label htmlFor="artist_statement" className="submission-form-label">
                {submissionData.form_config.sections.artist_statement.label || 'Artist Statement'}
                {submissionData.form_config.sections.artist_statement.required ? ' *' : ' (Optional)'}
                {submissionData.form_config.sections.artist_statement.max_length && 
                  ` - Max ${submissionData.form_config.sections.artist_statement.max_length} characters`
                }
              </label>
              <div className="relative">
                <FileText className="absolute left-3 top-3 text-gray-400 w-4 h-4" />
                <textarea
                  id="artist_statement"
                  rows={4}
                  value={formData.artist_statement || ''}
                  onChange={(e) => handleInputChange('artist_statement', e.target.value)}
                  className={`submission-form-input submission-form-textarea pl-10 ${
                    errors.artist_statement ? 'error' : ''
                  }`}
                  placeholder={submissionData.form_config.sections.artist_statement.placeholder || 
                    "Describe your artistic practice, inspiration, and what drives your creative work..."}
                  maxLength={submissionData.form_config.sections.artist_statement.max_length || 200}
                />
              </div>
              <div className="mt-2 flex items-center justify-between">
                {errors.artist_statement && (
                  <p className="submission-form-error">
                    <AlertCircle className="w-4 h-4 mr-1" />
                    {errors.artist_statement}
                  </p>
                )}
                {submissionData.form_config.sections.artist_statement.max_length && (
                  <span className={`text-sm ${
                    (formData.artist_statement?.length || 0) > (submissionData.form_config.sections.artist_statement.max_length * 0.9) ? 'text-orange-600' : 'text-gray-500'
                  }`}>
                    {(formData.artist_statement?.length || 0)}/{submissionData.form_config.sections.artist_statement.max_length}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Professional Information */}
      {submissionData.form_config?.sections?.professional_info?.enabled && (
        <div className="submission-content-card">
          <div className="submission-content-card-header">
            <h3 className="submission-content-card-title">Professional Information</h3>
          </div>
          
          <div className="submission-content-card-body">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Preferred Mediums */}
              {submissionData.form_config.sections.professional_info.fields?.preferred_mediums?.enabled && (
                <div className="submission-form-group">
                  <label className="submission-form-label">
                    Preferred Mediums
                    {submissionData.form_config.sections.professional_info.fields.preferred_mediums.required ? ' *' : ' (Optional)'}
                  </label>
                  <div className="checkbox-group">
                    {(submissionData.form_config.sections.professional_info.fields.preferred_mediums.options || 
                      ['Painting', 'Sculpture', 'Photography', 'Digital Art', 'Mixed Media', 'Printmaking', 'Drawing', 'Installation', 'Video', 'Performance']
                    ).map((medium) => (
                      <div key={medium} className="checkbox-item">
                        <input
                          type="checkbox"
                          id={`medium-${medium}`}
                          checked={(formData.preferred_mediums || []).includes(medium)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              handleArrayChange('preferred_mediums', [...(formData.preferred_mediums || []), medium], 'set');
                            } else {
                              handleArrayChange('preferred_mediums', medium, 'remove');
                            }
                          }}
                        />
                        <label htmlFor={`medium-${medium}`}>{medium}</label>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Years of Experience */}
              {submissionData.form_config.sections.professional_info.fields?.years_experience?.enabled && (
                <div className="submission-form-group">
                  <label htmlFor="years_experience" className="submission-form-label">
                    Years of Experience
                    {submissionData.form_config.sections.professional_info.fields.years_experience.required ? ' *' : ' (Optional)'}
                  </label>
                  <select
                    id="years_experience"
                    value={formData.years_experience || ''}
                    onChange={(e) => handleInputChange('years_experience', e.target.value)}
                    className="submission-form-input submission-form-select"
                  >
                    <option value="">Select experience level</option>
                    {(submissionData.form_config.sections.professional_info.fields.years_experience.options || 
                      ['0-2', '3-5', '6-10', '11-15', '16+']
                    ).map((option) => (
                      <option key={option} value={option}>{option} years</option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            {/* Education */}
            {submissionData.form_config.sections.professional_info.fields?.education?.enabled && (
              <div className="submission-form-group">
                <label htmlFor="education" className="submission-form-label">
                  Education
                  {submissionData.form_config.sections.professional_info.fields.education.required ? ' *' : ' (Optional)'}
                </label>
                <textarea
                  id="education"
                  rows={3}
                  value={formData.education || ''}
                  onChange={(e) => handleInputChange('education', e.target.value)}
                  className="submission-form-input submission-form-textarea"
                  placeholder="List your relevant education, degrees, and training..."
                  maxLength={submissionData.form_config.sections.professional_info.fields.education.max_length || 500}
                />
              </div>
            )}
          </div>
        </div>
      )}

      {/* Awards & Recognition */}
      {submissionData.form_config?.sections?.awards?.enabled && (
        <div className="submission-content-card">
          <div className="submission-content-card-header">
            <h3 className="submission-content-card-title">
              {submissionData.form_config.sections.awards.label || 'Awards & Recognition'}
            </h3>
          </div>
          
          <div className="submission-content-card-body">
            <div className="submission-form-group">
              <label className="submission-form-label">
                {submissionData.form_config.sections.awards.label || 'Awards & Recognition'}
                {submissionData.form_config.sections.awards.required ? ' *' : ' (Optional)'}
              </label>
              <div className="space-y-2">
                {(formData.awards || []).map((award, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    <input
                      type="text"
                      value={award}
                      onChange={(e) => {
                        const newAwards = [...(formData.awards || [])];
                        newAwards[index] = e.target.value;
                        handleArrayChange('awards', newAwards, 'set');
                      }}
                      className="submission-form-input flex-1"
                      placeholder="Enter award or recognition"
                    />
                    <button
                      type="button"
                      onClick={() => handleArrayChange('awards', index, 'remove')}
                      className="submission-btn submission-btn-danger"
                    >
                      Remove
                    </button>
                  </div>
                ))}
                {(!submissionData.form_config.sections.awards.max_count || 
                  (formData.awards || []).length < submissionData.form_config.sections.awards.max_count) && (
                  <button
                    type="button"
                    onClick={() => handleArrayChange('awards', '', 'add')}
                    className="submission-btn submission-btn-secondary"
                  >
                    + Add Award
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Notable Exhibitions */}
      {submissionData.form_config?.sections?.exhibitions?.enabled && (
        <div className="submission-content-card">
          <div className="submission-content-card-header">
            <h3 className="submission-content-card-title">
              {submissionData.form_config.sections.exhibitions.label || 'Notable Exhibitions'}
            </h3>
          </div>
          
          <div className="submission-content-card-body">
            <div className="submission-form-group">
              <label className="submission-form-label">
                {submissionData.form_config.sections.exhibitions.label || 'Notable Exhibitions'}
                {submissionData.form_config.sections.exhibitions.required ? ' *' : ' (Optional)'}
              </label>
              <div className="space-y-2">
                {(formData.exhibitions || []).map((exhibition, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    <input
                      type="text"
                      value={exhibition}
                      onChange={(e) => {
                        const newExhibitions = [...(formData.exhibitions || [])];
                        newExhibitions[index] = e.target.value;
                        handleArrayChange('exhibitions', newExhibitions, 'set');
                      }}
                      className="submission-form-input flex-1"
                      placeholder="Enter exhibition name and venue"
                    />
                    <button
                      type="button"
                      onClick={() => handleArrayChange('exhibitions', index, 'remove')}
                      className="submission-btn submission-btn-danger"
                    >
                      Remove
                    </button>
                  </div>
                ))}
                {(!submissionData.form_config.sections.exhibitions.max_count || 
                  (formData.exhibitions || []).length < submissionData.form_config.sections.exhibitions.max_count) && (
                  <button
                    type="button"
                    onClick={() => handleArrayChange('exhibitions', '', 'add')}
                    className="submission-btn submission-btn-secondary"
                  >
                    + Add Exhibition
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApplicationForm;
